/**
 * Sanitize a note's stored HTML before it is displayed.
 *
 * Note content is authored in the react-rte editor and stored as raw HTML,
 * then rendered via `dangerouslySetInnerHTML` in the notes-list previews.
 * Because notes are shared between instructors and students, that render path
 * is an XSS surface once hyperlinks (hrefs) are allowed. This helper neutralizes
 * dangerous content and makes safe links open in a new tab.
 *
 * - Element allowlist: only the tags react-rte legitimately emits survive.
 *   Everything else — <script>, <style>, <iframe>, <object>, <embed>, <svg>,
 *   <form>, <img>, etc. — is removed with its subtree. An allowlist (vs a
 *   denylist of known-bad tags) is what closes stored-XSS vectors like
 *   `<iframe src="javascript:…">` that a denylist silently misses.
 * - Strips any inline `on*` event-handler attrs from surviving elements.
 * - Anchors: only http:/https:/mailto: are honored. Any other scheme (notably
 *   javascript:/data:) has its href removed so the text is inert. Safe links get
 *   target="_blank" rel="noopener noreferrer nofollow".
 *
 * Scheme detection reads the anchor element's own `.protocol`, which resolves
 * relative URLs against the document base and is robust to obfuscation
 * (e.g. `java\tscript:`), unlike a naive string match.
 *
 * NOTE: this is a pragmatic, dependency-free sanitizer for react-rte's narrow
 * output. For a broader/adversarial HTML surface, prefer a vetted library
 * (DOMPurify).
 *
 * @param {string} html
 * @returns {string} HTML safe to pass to dangerouslySetInnerHTML
 */
const SAFE_PROTOCOLS = ['http:', 'https:', 'mailto:'];

// Tags a note legitimately contains (react-rte: formatting, headings, lists,
// links). Anything outside this set is stripped.
const ALLOWED_TAGS = new Set([
    'p', 'br', 'span', 'div',
    'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'blockquote', 'pre', 'code',
    'a',
]);

export default function sanitizeNoteHtml(html) {
    if (!html || typeof html !== 'string') {
        return '';
    }

    const div = document.createElement('div');
    div.innerHTML = html;

    // Remove any element not on the allowlist, along with its subtree.
    // querySelectorAll returns nodes in document order (parents before
    // children), so removing a disallowed parent also drops its descendants;
    // a later .remove() on an already-detached node is a harmless no-op.
    div.querySelectorAll('*').forEach((el) => {
        if (!ALLOWED_TAGS.has(el.tagName.toLowerCase())) {
            el.remove();
        }
    });

    // Remove any inline event-handler attributes (onclick, onerror, ...).
    div.querySelectorAll('*').forEach((el) => {
        Array.prototype.slice.call(el.attributes).forEach((attr) => {
            if (/^on/i.test(attr.name)) {
                el.removeAttribute(attr.name);
            }
        });
    });

    // Sanitize anchors.
    div.querySelectorAll('a').forEach((a) => {
        let safe = false;
        try {
            safe = a.hasAttribute('href') && SAFE_PROTOCOLS.indexOf(a.protocol) !== -1;
        } catch (e) {
            safe = false;
        }

        if (safe) {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer nofollow');
        } else {
            a.removeAttribute('href');
            a.removeAttribute('target');
        }
    });

    return div.innerHTML;
}
